import {
  DEFAULT_SCANNER_EXCHANGE,
  DEFAULT_SCANNER_TIMEFRAME,
  DEFAULT_SCANNER_UNIVERSE,
} from './scannerExperienceShared';

const SCANNER_ANONYMOUS_LIMIT_HEADER = 'X-Scanner-Anonymous-Limit-Applied';
const SCANNER_ANALYZED_COUNT_HEADER = 'X-Scanner-Analyzed-Count';

function normalizeBoolean(value) {
  return value ? 'true' : 'false';
}

function ensureOk(response, fallbackMessage) {
  if (response.ok) {
    return response;
  }
  throw new Error(fallbackMessage);
}

function parseOptionalInteger(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

export async function fetchScannerOverview(filters = {}, options = {}) {
  const query = new URLSearchParams({
    universe: filters.universe || DEFAULT_SCANNER_UNIVERSE,
    exchange: filters.exchange || DEFAULT_SCANNER_EXCHANGE,
    timeframe: filters.timeframe || DEFAULT_SCANNER_TIMEFRAME,
    bullishOnly: normalizeBoolean(filters.direction === 'BULLISH'),
    bearishOnly: normalizeBoolean(filters.direction === 'BEARISH'),
    topMoversOnly: normalizeBoolean(filters.topMoversOnly),
    forceRefresh: normalizeBoolean(options.forceRefresh),
  });

  if (Number.isFinite(Number(filters.patternEngineVersion))) {
    query.set('patternEngineVersion', String(Number(filters.patternEngineVersion)));
  }

  const response = ensureOk(
    await fetch(`/api/scanner?${query.toString()}`),
    'Scanner indisponible',
  );
  const rows = await response.json();
  const anonymousLimitHeader = response.headers.get(SCANNER_ANONYMOUS_LIMIT_HEADER);
  const analyzedCountHeader = response.headers.get(SCANNER_ANALYZED_COUNT_HEADER);
  return {
    rows: Array.isArray(rows) ? rows : [],
    anonymousLimitApplied: anonymousLimitHeader === null ? null : anonymousLimitHeader === 'true',
    analyzedAssetCount: parseOptionalInteger(analyzedCountHeader),
  };
}

export async function fetchScannerDetail(symbol, options = {}) {
  const query = new URLSearchParams({
    exchange: options.exchange || DEFAULT_SCANNER_EXCHANGE,
    timeframe: options.timeframe || DEFAULT_SCANNER_TIMEFRAME,
    candles: String(options.candles || 450),
    imageCandles: String(options.imageCandles || 120),
    historyLimit: String(options.historyLimit || 10),
  });

  if (Number.isFinite(Number(options.patternEngineVersion))) {
    query.set('patternEngineVersion', String(Number(options.patternEngineVersion)));
  }
  if (Number.isFinite(Number(options.tradeEngineVersion))) {
    query.set('tradeEngineVersion', String(Number(options.tradeEngineVersion)));
  }

  const response = ensureOk(
    await fetch(`/api/scanner/${encodeURIComponent(symbol)}?${query.toString()}`),
    'Analyse indisponible',
  );
  return response.json();
}

export async function fetchTradePlan(symbol, options = {}) {
  const query = new URLSearchParams({
    exchange: options.exchange || DEFAULT_SCANNER_EXCHANGE,
    timeframe: options.timeframe || DEFAULT_SCANNER_TIMEFRAME,
    candles: String(options.candles || 450),
    imageCandles: String(options.imageCandles || 120),
  });

  if (Number.isFinite(Number(options.patternEngineVersion))) {
    query.set('patternEngineVersion', String(Number(options.patternEngineVersion)));
  }
  if (Number.isFinite(Number(options.tradeEngineVersion))) {
    query.set('tradeEngineVersion', String(Number(options.tradeEngineVersion)));
  }

  const response = ensureOk(
    await fetch(`/api/scanner/${encodeURIComponent(symbol)}/trade-plan?${query.toString()}`),
    'Trade plan indisponible',
  );
  return response.json();
}

export async function fetchAssetMetricSnapshot(symbol) {
  const response = ensureOk(
    await fetch(`/api/asset-metrics/${encodeURIComponent(symbol)}/latest`),
    'Snapshot Asset Scanner indisponible',
  );
  return response.json();
}

export async function fetchAssetMetricHistory(symbol) {
  const response = ensureOk(
    await fetch(`/api/asset-metrics/${encodeURIComponent(symbol)}/history`),
    'Historique Asset Scanner indisponible',
  );
  const payload = await response.json();
  return Array.isArray(payload) ? payload : [];
}

export async function recordPatternScannerPageView(options = {}) {
  const query = new URLSearchParams({
    timeframe: options.timeframe || DEFAULT_SCANNER_TIMEFRAME,
  });

  if (options.asset) {
    query.set('asset', options.asset);
  }

  try {
    await fetch(`/api/scanner/page-views?${query.toString()}`, {
      method: 'POST',
      credentials: 'include',
      signal: options.signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') {
      return;
    }
    // Ignore logging failures: the page itself should stay usable.
  }
}

export function buildPatternImageRequestUrl(analysis, options = {}) {
  const query = new URLSearchParams();
  const { forceSymbolLookup = false, patternEngineVersion } = options;
  if (!forceSymbolLookup && analysis?.imagePath) {
    query.set('imagePath', analysis.imagePath);
  }
  if (analysis?.symbol) {
    query.set('symbol', analysis.symbol);
  }
  if (analysis?.exchange) {
    query.set('exchange', analysis.exchange);
  }
  if (analysis?.timeframe) {
    query.set('timeframe', analysis.timeframe);
  }
  if (analysis?.imageVersion) {
    query.set('version', analysis.imageVersion);
  }
  if (analysis?.imageGeneratedAt) {
    query.set('generatedAt', analysis.imageGeneratedAt);
  }
  if (analysis?.analysisTimestamp) {
    query.set('analysisTimestamp', analysis.analysisTimestamp);
  }
  if (Number.isFinite(Number(patternEngineVersion))) {
    query.set('patternEngineVersion', String(Number(patternEngineVersion)));
  }
  return `/api/pattern/image?${query.toString()}`;
}

export async function fetchPatternImageAsBlob(analysis, options = {}) {
  const { signal, forceSymbolLookup = false, patternEngineVersion } = options;
  const response = await fetch(
    buildPatternImageRequestUrl(analysis, { forceSymbolLookup, patternEngineVersion }),
    signal ? { signal } : undefined,
  );
  if (!response.ok) {
    return null;
  }
  return response.blob();
}
